// Phase 24c · Admin subscription dashboard
import React, { useEffect, useState, useCallback } from "react";
import { BarChart3, ListChecks, AlertTriangle, Gift, Download, CheckCircle, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function eur(cents) { return `$${((cents || 0) / 100).toFixed(2)}`; }
function fmt(dt) { if (!dt) return ""; try { return new Date(dt).toLocaleDateString(); } catch { return dt; } }

const TABS = [
    { id: "overview", label: "Overview", Icon: BarChart3 },
    { id: "all", label: "All subs", Icon: ListChecks },
    { id: "pending", label: "Pending review", Icon: AlertTriangle },
    { id: "dunning", label: "Dunning", Icon: AlertTriangle },
    { id: "grants", label: "Grants", Icon: Gift },
];

export default function AdminSubscriptions() {
    const [tab, setTab] = useState("overview");
    const [overview, setOverview] = useState(null);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [grantForm, setGrantForm] = useState({user_id:"", tier_slug:"premium_feed", interval:"month", months:1, reason:"manual_grant"});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            if (tab === "overview") {
                const r = await fetch(`${API}/api/admin/subscriptions/overview`, { credentials: "include" });
                if (r.ok) setOverview(await r.json());
            } else {
                const q = tab === "pending" ? "?pending_review=true"
                        : tab === "dunning" ? "?dunning_state=grace"
                        : tab === "grants" ? "?pending_review=true"
                        : "";
                const r = await fetch(`${API}/api/admin/subscriptions/list${q}&limit=100`, { credentials: "include" });
                if (r.ok) setList(((await r.json()) || {}).items || []);
            }
        } catch {}
        setLoading(false);
    }, [tab]);

    useEffect(() => { document.title = "Admin · Subscriptions"; load(); }, [load]);

    const approve = async (id) => {
        const r = await fetch(`${API}/api/admin/subscriptions/${id}/approve-review`, {
            method: "POST", credentials: "include",
        });
        if (r.ok) load();
    };
    const deny = async (id) => {
        const reason = window.prompt("Deny reason:", "review_denied");
        if (!reason) return;
        const r = await fetch(`${API}/api/admin/subscriptions/${id}/deny-review`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason }),
        });
        if (r.ok) load();
    };
    const forceCancel = async (id) => {
        const reason = window.prompt("Force cancel reason (required):", "emergency_cancel");
        if (!reason) return;
        const r = await fetch(`${API}/api/admin/subscriptions/${id}/force-cancel`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reason }),
        });
        if (r.ok) load();
    };
    const grant = async () => {
        const r = await fetch(`${API}/api/admin/subscriptions/grant`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(grantForm),
        });
        if (r.ok) load();
    };
    const triggerCron = async (which) => {
        const r = await fetch(`${API}/api/admin/subscriptions/cron/${which}/trigger`, {
            method: "POST", credentials: "include",
        });
        if (r.ok) {
            const d = await r.json();
            alert(`${which} cron: ${JSON.stringify(d)}`);
            load();
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
            <div className="max-w-7xl mx-auto px-4 py-6">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold" data-testid="admin-subs-title">Subscriptions</h1>
                        <p className="text-xs text-neutral-500 mt-0.5">Phase 24c · Premium lifecycle + Operator Layer</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => triggerCron("renewal")} data-testid="admin-subs-cron-renewal" className="text-xs px-3 py-1.5 rounded-full border border-[#1A1A1A] hover:border-[#CCCCCC]/50">
                            Trigger renewal
                        </button>
                        <button onClick={() => triggerCron("dunning")} data-testid="admin-subs-cron-dunning" className="text-xs px-3 py-1.5 rounded-full border border-[#1A1A1A] hover:border-[#CCCCCC]/50">
                            Trigger dunning
                        </button>
                        <a href={`${API}/api/admin/subscriptions/audit-log/export.csv`}
                            data-testid="admin-subs-csv-export"
                            className="text-xs px-3 py-1.5 rounded-full border border-[#CCCCCC]/40 hover:bg-[#CCCCCC]/10 inline-flex items-center gap-1">
                            <Download size={12} /> CSV
                        </a>
                    </div>
                </div>

                <div className="flex flex-wrap gap-1 mb-6 border-b border-[#1A1A1A]" data-testid="admin-subs-tabs">
                    {TABS.map(({ id, label, Icon }) => (
                        <button key={id} onClick={() => setTab(id)}
                            data-testid={`admin-subs-tab-${id}`}
                            className={`text-xs px-3 py-2 inline-flex items-center gap-1.5 transition ${
                                tab === id ? "text-[#CCCCCC] border-b-2 border-[#CCCCCC]" : "text-neutral-500 hover:text-neutral-300"
                            }`}>
                            <Icon size={12} /> {label}
                        </button>
                    ))}
                </div>

                {loading && <div className="text-sm text-neutral-500 mb-3">Loading…</div>}

                {tab === "overview" && overview && (
                    <div className="space-y-4" data-testid="admin-subs-overview">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">MRR</p>
                                <p className="text-2xl font-bold text-[#CCCCCC]" data-testid="kpi-mrr">{eur(overview.mrr_cents)}</p>
                            </div>
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">ARR</p>
                                <p className="text-2xl font-bold text-[#CCCCCC]" data-testid="kpi-arr">{eur(overview.arr_cents)}</p>
                            </div>
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">Active subs</p>
                                <p className="text-2xl font-bold" data-testid="kpi-active">{overview.active_total}</p>
                                <p className="text-[10px] text-neutral-500">Churn this month: {overview.churn_this_month}</p>
                            </div>
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">Pending review</p>
                                <p className="text-2xl font-bold text-neutral-200" data-testid="kpi-pending">{overview.pending_review}</p>
                            </div>
                        </div>
                        <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-2">Active per tier</p>
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                                {Object.entries(overview.active_per_tier || {}).map(([k,v]) => (
                                    <div key={k} className="rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] p-2">
                                        <p className="text-[10px] text-neutral-500 truncate">{k.replace(/^premium_/,'')}</p>
                                        <p className="text-lg font-semibold">{v}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-2">Dunning state</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                {Object.entries(overview.dunning_stats || {}).map(([k,v]) => (
                                    <div key={k} className="rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] p-2">
                                        <p className="text-[10px] text-neutral-500 truncate">{k}</p>
                                        <p className="text-lg font-semibold">{v}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {(tab === "all" || tab === "pending" || tab === "dunning") && (
                    <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] overflow-x-auto" data-testid={`admin-subs-table-${tab}`}>
                        <table className="w-full text-xs">
                            <thead className="text-left text-neutral-500 border-b border-[#1A1A1A]">
                                <tr>
                                    <th className="px-3 py-2">stripe_id</th>
                                    <th className="px-3 py-2">user_id</th>
                                    <th className="px-3 py-2">tier</th>
                                    <th className="px-3 py-2">status</th>
                                    <th className="px-3 py-2">dunning</th>
                                    <th className="px-3 py-2">next period end</th>
                                    <th className="px-3 py-2">actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((s) => (
                                    <tr key={s.stripe_id} className="border-b border-[#1A1A1A]" data-testid={`sub-admin-row-${s.stripe_id}`}>
                                        <td className="px-3 py-2 font-mono text-[10px] text-neutral-500 truncate max-w-[180px]">{s.stripe_id}</td>
                                        <td className="px-3 py-2 font-mono text-[10px] text-neutral-500 truncate max-w-[140px]">{s.user_id}</td>
                                        <td className="px-3 py-2">{s.tier_slug}</td>
                                        <td className="px-3 py-2 capitalize">{s.status}</td>
                                        <td className="px-3 py-2">{s.dunning_state}</td>
                                        <td className="px-3 py-2 text-neutral-500">{fmt(s.current_period_end)}</td>
                                        <td className="px-3 py-2">
                                            {s.pending_operator_review ? (
                                                <div className="flex gap-1">
                                                    <button onClick={() => approve(s.stripe_id)}
                                                        data-testid={`sub-admin-approve-${s.stripe_id}`}
                                                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[#CCCCCC]/15 text-[#CCCCCC]">
                                                        <CheckCircle size={10} /> Approve
                                                    </button>
                                                    <button onClick={() => deny(s.stripe_id)}
                                                        data-testid={`sub-admin-deny-${s.stripe_id}`}
                                                        className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-[#FAFAFA]/15 text-[#FAFAFA]">
                                                        <XCircle size={10} /> Deny
                                                    </button>
                                                </div>
                                            ) : s.status === "active" ? (
                                                <button onClick={() => forceCancel(s.stripe_id)}
                                                    data-testid={`sub-admin-force-cancel-${s.stripe_id}`}
                                                    className="text-[10px] px-2 py-1 rounded-full border border-[#FAFAFA]/40 text-[#FAFAFA]">
                                                    Force cancel
                                                </button>
                                            ) : <span className="text-[10px] text-neutral-500">—</span>}
                                        </td>
                                    </tr>
                                ))}
                                {list.length === 0 && (
                                    <tr><td colSpan="7" className="px-3 py-6 text-center text-neutral-500">No results</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "grants" && (
                    <div className="space-y-4" data-testid="admin-subs-grants">
                        <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-3">Grant a subscription</p>
                            <div className="flex flex-wrap gap-2">
                                <input placeholder="user_id" value={grantForm.user_id}
                                    onChange={e => setGrantForm({...grantForm, user_id: e.target.value})}
                                    data-testid="grant-form-user"
                                    className="text-xs bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-2 py-1.5 w-64 font-mono" />
                                <select value={grantForm.tier_slug}
                                    onChange={e => setGrantForm({...grantForm, tier_slug: e.target.value})}
                                    data-testid="grant-form-tier"
                                    className="text-xs bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-2 py-1.5">
                                    {["premium_feed","premium_groups","premium_rooms","premium_events","premium_creator"].map(t =>
                                        <option key={t} value={t}>{t}</option>)}
                                </select>
                                <select value={grantForm.interval}
                                    onChange={e => setGrantForm({...grantForm, interval: e.target.value})}
                                    data-testid="grant-form-interval"
                                    className="text-xs bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-2 py-1.5">
                                    <option value="month">monthly</option>
                                    <option value="year">annual</option>
                                </select>
                                <input placeholder="reason" value={grantForm.reason}
                                    onChange={e => setGrantForm({...grantForm, reason: e.target.value})}
                                    data-testid="grant-form-reason"
                                    className="text-xs bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-2 py-1.5 w-48" />
                                <button onClick={grant} data-testid="grant-form-submit"
                                    className="text-xs px-4 py-1.5 rounded-full bg-neutral-100 text-neutral-900 hover:bg-neutral-200">
                                    Grant
                                </button>
                            </div>
                            <p className="text-[11px] text-neutral-500 mt-2">Grants land in <em>pending review</em> — a second admin confirms via Pending Review tab.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
